import React, { useEffect, useState, useRef, useCallback } from 'react';
import useGameBase from '../../hooks/useGameBase';

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

const BOARD_SIZE = 288; // px (w-72 h-72)
const SECTOR_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

// SVG ring thresholds derived from actual circle radii in dartboard.svg (all / 226)
const INNER_BULL  = 6.85  / 226; // 0.0303
const OUTER_BULL  = 16.4  / 226; // 0.0726
const TRIPLE_IN   = 97.5  / 226; // 0.4314
const TRIPLE_OUT  = 106.5 / 226; // 0.4712
const DOUBLE_IN   = 160.5 / 226; // 0.7102
const DOUBLE_OUT  = 169.5 / 226; // 0.7500

const RING_BANDS = [
  { inner: 0,          outer: INNER_BULL,  ring: 'INNER_BULL' },
  { inner: INNER_BULL, outer: OUTER_BULL,  ring: 'OUTER_BULL' },
  { inner: OUTER_BULL, outer: TRIPLE_IN,   ring: 'SINGLE' },
  { inner: TRIPLE_IN,  outer: TRIPLE_OUT,  ring: 'TRIPLE' },
  { inner: TRIPLE_OUT, outer: DOUBLE_IN,   ring: 'SINGLE' },
  { inner: DOUBLE_IN,  outer: DOUBLE_OUT,  ring: 'DOUBLE' },
  { inner: DOUBLE_OUT, outer: Infinity,    ring: 'MISS'   },
];

/**
 * Detect ring + sector from a percentage position {x, y} (0-100).
 * Thresholds match the actual SVG geometry.
 */
function detectRingAndSector(pos) {
  const dx = pos.x - 50;
  const dy = pos.y - 50;
  const dist = Math.sqrt(dx * dx + dy * dy);
  let r = dist / 50; // 0=center, 1=edge of image

  // Nudge r off ring boundaries (within 0.002 normalised units)
  const RING_NUDGE = 0.002;
  for (const band of RING_BANDS) {
    if (Math.abs(r - band.inner) < RING_NUDGE) { r = band.inner + RING_NUDGE; break; }
    if (band.outer !== Infinity && Math.abs(r - band.outer) < RING_NUDGE) { r = band.outer - RING_NUDGE; break; }
  }

  let ring = 'MISS';
  for (const band of RING_BANDS) {
    if (r >= band.inner && r < band.outer) { ring = band.ring; break; }
  }

  // Sector detection
  let angleDeg = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
  let rotated = (angleDeg + 90) % 360; // 0=top

  const SLICE = 18;
  const posInSlice = rotated % SLICE;
  const distToSectorBoundary = Math.min(posInSlice, SLICE - posInSlice);
  if (distToSectorBoundary < 1) {
    rotated = (rotated + (posInSlice < SLICE / 2 ? -0.5 : 0.5) + 360) % 360;
  }

  const sectorIndex = Math.floor((rotated + SLICE / 2) / SLICE) % 20;
  const sector = SECTOR_ORDER[sectorIndex];

  return { ring, sector };
}

function ringLabel(ring, sector) {
  if (ring === 'MISS') return 'Miss';
  if (ring === 'INNER_BULL' || ring === 'OUTER_BULL') return 'Bull';
  const prefix = ring === 'DOUBLE' ? 'D' : ring === 'TRIPLE' ? 'T' : '';
  return `${prefix}${sector}`;
}

// -------------------------------------------------------------------
// Component
// -------------------------------------------------------------------

export default function DartsGame({ gameState, gamePlayers, user, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const [phase, setPhase] = useState('idle'); // 'idle' | 'timing' | 'waiting'
  const [targetPos, setTargetPos] = useState(null);
  const [barPos, setBarPos] = useState(50);
  const [flyingDarts, setFlyingDarts] = useState([]);

  const lastAnimatedTsRef = useRef(0); // timestamp of the last throw we started animating
  const rafRef = useRef(null);
  const sliderRafRef = useRef(null);
  const sliderStateRef = useRef({ position: 50, direction: 1, speed: 2 });
  const boardRef = useRef(null);
  const prevPlayerRef = useRef(null);

  const boardUrl = '/images/darts/dartboard.svg';

  const myId = user?.socketId || user?.userId;
  const isFinished = gameState?.finished === true;
  const currentPlayer = gameState?.currentPlayer || gameState?.currentPlayerSocketId;
  // When finished, the winner is the last active player (currentPlayer becomes null on win)
  const activePlayer = isFinished
    ? (gameState?.winners?.[0] ?? null)
    : currentPlayer;
  const isMyTurn = !!myId && currentPlayer === myId;

  const turnThrowsRemaining = gameState?.turnThrowsRemaining ?? 3;
  const throwsDoneThisTurn = Math.max(0, 3 - turnThrowsRemaining);

  const gameMode = gameState?.gameMode || gameState?.config?.gameMode || '301';
  const isAroundTheWorld = gameMode === 'aroundTheWorld';
  const atwTargetLabel = (target) => target > 21 ? '✓' : target === 21 ? 'Bull' : `${target}`;

  // Current turn throws: throws by activePlayer on/after the current turn started.
  // When finished, activePlayer is the winner so the winning turn's darts stay shown.
  const turnStartedAt = gameState?.turnStartedAt ?? 0;
  const currentTurnThrows = (() => {
    if (!gameState || !Array.isArray(gameState.recentThrows) || !activePlayer) return [];
    return gameState.recentThrows.filter(
      t => t.playerSocketId === activePlayer && t.timestamp >= turnStartedAt
    );
  })();

  // On finish, never reset the board — keep the winning turn's darts displayed.
  // The reset effect only fires for genuine new turns (turnStartedAt changes).

  // Reset local state whenever a new turn starts (covers same-player repeat turns too).
  // Never fires when the game is finished — winning darts must stay on the board.
  const prevTurnStartedAtRef = useRef(0);
  useEffect(() => {
    if (isFinished) return; // don't clear board on win
    if (turnStartedAt !== prevTurnStartedAtRef.current) {
      prevTurnStartedAtRef.current = turnStartedAt;
      prevPlayerRef.current = currentPlayer;
      lastAnimatedTsRef.current = 0;
      setPhase('idle');
      setTargetPos(null);
      setFlyingDarts([]);
      if (sliderRafRef.current) cancelAnimationFrame(sliderRafRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
  }, [turnStartedAt, currentPlayer, isFinished]);

  // Dart flight animation — fires for ALL players whenever a new throw lands.
  // Uses a timestamp ref so it's safe under React StrictMode (double-invoke cleanup
  // resets the ref, allowing the real second mount to re-animate correctly).
  useEffect(() => {
    if (!activePlayer || !Array.isArray(gameState?.recentThrows)) return;

    const playerThrows = gameState.recentThrows.filter(
      t => t.playerSocketId === activePlayer && t.timestamp >= turnStartedAt
    );
    if (playerThrows.length === 0) return;

    const newest = playerThrows[playerThrows.length - 1];
    if (newest.timestamp <= lastAnimatedTsRef.current) return; // already animated

    lastAnimatedTsRef.current = newest.timestamp;

    const endPos = newest.position || { x: 50, y: 50 };
    const id = `${newest.playerSocketId}-${newest.timestamp}`;
    const startTime = Date.now();
    const duration = 300;
    let cancelled = false;

    const animate = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startTime;
      const t = Math.min(1, elapsed / duration);
      if (t < 1) {
        const x = 50 + (endPos.x - 50) * t;
        const y = 50 + (endPos.y - 50) * t;
        const scale = 4 - t * 3.4;
        setFlyingDarts(prev => [...prev.filter(d => d.id !== id), { id, x, y, scale }]);
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setFlyingDarts(prev => prev.filter(d => d.id !== id));
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Reset timestamp on cleanup so a re-run (StrictMode or rapid update) can re-animate.
      lastAnimatedTsRef.current = 0;
    };
  }, [gameState?.recentThrows, activePlayer, turnStartedAt]);

  const startSlider = useCallback(() => {
    sliderStateRef.current = { position: 50, direction: 1, speed: 2 };
    const tick = () => {
      const s = sliderStateRef.current;
      s.position += s.speed * s.direction;
      if (s.position >= 100) { s.position = 100; s.direction = -1; }
      if (s.position <= 0) { s.position = 0; s.direction = 1; }
      setBarPos(s.position);
      sliderRafRef.current = requestAnimationFrame(tick);
    };
    sliderRafRef.current = requestAnimationFrame(tick);
  }, []);

  const handleBoardClick = (e) => {
    if (!isMyTurn || phase !== 'idle' || throwsDoneThisTurn >= 3) return;
    const rect = boardRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setTargetPos({ x, y });
    setPhase('timing');
    setBarPos(50);
    startSlider();
  };

  const handleSliderClick = () => {
    if (phase !== 'timing') return;
    if (sliderRafRef.current) cancelAnimationFrame(sliderRafRef.current);

    const pos = sliderStateRef.current.position;
    const distFromCenter = Math.abs(pos - 50);
    const accuracy = 100 * Math.pow(Math.max(0, 50 - distFromCenter) / 50, 2);
    const deviationDirection = pos - 50;

    setPhase('waiting');

    const { ring, sector } = detectRingAndSector(targetPos);

    const action = {
      type: 'throw',
      targetPos,
      accuracy,
      deviationDirection,
      throwingStyle: 'singleBar',
      targetRing: ring,
      targetSector: sector,
    };

    try {
      if (typeof onGameAction === 'function') onGameAction(action);
      else submitAction(action);
    } catch (err) {
      console.error('Throw failed:', err);
    }

    setTimeout(() => setPhase('idle'), 300);
  };

  // Crosshair marker
  const Crosshair = ({ x, y, size = 6, lineW = 1.5, color = '#FDE047', glow = '0 0 0 0.5px black, 0 0 3px rgba(255,255,0,0.6)' }) => (
    <div style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)', pointerEvents: 'none', zIndex: 10 }}>
      <div style={{ position: 'relative', width: 0, height: 0 }}>
        <div style={{ position: 'absolute', width: size, height: lineW, backgroundColor: color, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', boxShadow: glow }} />
        <div style={{ position: 'absolute', width: lineW, height: size, backgroundColor: color, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', boxShadow: glow }} />
      </div>
    </div>
  );

  const currentPlayerObj = (gameState?.players || []).find(p => p.socketId === activePlayer);

  return (
    <div className="darts-game w-full h-full flex flex-col items-center justify-start p-4 bg-gray-100 dark:bg-gray-900 gap-4">

      {/* Board */}
      <div className="relative" style={{ width: BOARD_SIZE, height: BOARD_SIZE }}>
        <div
          className="relative rounded-full overflow-hidden shadow-lg"
          style={{ width: BOARD_SIZE, height: BOARD_SIZE }}
          ref={boardRef}
        >
          <img
            src={boardUrl}
            alt="Dartboard"
            className={`w-full h-full object-cover select-none ${isMyTurn && phase === 'idle' && throwsDoneThisTurn < 3 ? 'cursor-crosshair' : 'cursor-default'}`}
            onClick={handleBoardClick}
            draggable={false}
          />

          {/* Aim dot while timing */}
          {targetPos && phase === 'timing' && (
            <div style={{ position: 'absolute', left: `${targetPos.x}%`, top: `${targetPos.y}%`, transform: 'translate(-50%,-50%)', pointerEvents: 'none', zIndex: 20 }}>
              <div className="w-3 h-3 rounded-full bg-yellow-400 ring-2 ring-white opacity-80" />
            </div>
          )}

          {/* Flying darts */}
          {flyingDarts.map(d => (
            <Crosshair key={d.id} x={d.x} y={d.y} size={Math.max(3, 6 * d.scale)} lineW={Math.max(1, 1.5 * Math.min(d.scale, 1))} />
          ))}

          {/* Landed darts for current turn */}
          {currentTurnThrows.map((t, i) => {
            const id = `${t.playerSocketId}-${t.timestamp}`;
            if (flyingDarts.some(d => d.id === id)) return null;
            const pos = t.position || { x: 50, y: 50 };
            // The last dart in a finished game is the winning throw — highlight it green
            const isWinner = isFinished && i === currentTurnThrows.length - 1;
            return <Crosshair
              key={id}
              x={pos.x} y={pos.y}
              size={isWinner ? 10 : 8}
              lineW={isWinner ? 2.5 : 2}
              color={isWinner ? '#4ade80' : '#FDE047'}
              glow={isWinner
                ? '0 0 0 1px black, 0 0 6px rgba(74,222,128,1), 0 0 12px rgba(74,222,128,0.6)'
                : '0 0 0 1px black, 0 0 4px rgba(255,255,0,0.8)'}
            />;
          })}

          {/* Winner overlay */}
          {isFinished && currentTurnThrows.length > 0 && (
            <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none" style={{ zIndex: 20 }}>
              <div className="bg-green-600 bg-opacity-90 text-white font-black text-lg px-5 py-1.5 rounded-full shadow-lg tracking-widest uppercase">
                🏆 {currentPlayerObj?.username || 'Winner'}!
              </div>
            </div>
          )}

          {/* Turn done overlay */}
          {isMyTurn && !isFinished && throwsDoneThisTurn >= 3 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
              <div className="text-center">
                <div className="text-xl font-bold text-white">Turn Done</div>
                <div className="text-sm text-gray-200">3 darts thrown</div>
              </div>
            </div>
          )}

          {/* Slider overlay - covers entire board, click anywhere to throw */}
          {isMyTurn && phase === 'timing' && (
            <div
              onClick={handleSliderClick}
              className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer select-none bg-black bg-opacity-50"
              style={{ zIndex: 30 }}
            >
              <div className="text-sm font-semibold text-white mb-2 drop-shadow">Click anywhere to throw!</div>
              <div className="relative w-48 h-7 bg-gray-800 bg-opacity-80 rounded overflow-hidden border border-gray-400">
                {/* Sweet-spot zone */}
                <div className="absolute top-0 h-full bg-green-400 opacity-40" style={{ left: '35%', width: '30%' }} />
                {/* Center line */}
                <div className="absolute top-0 h-full w-0.5 bg-green-400" style={{ left: '50%', transform: 'translateX(-50%)' }} />
                {/* Moving bar */}
                <div
                  className="absolute top-0 h-full w-2 bg-blue-400 rounded"
                  style={{ left: `${barPos}%`, transform: 'translateX(-50%)', transition: 'none' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Zoom preview - shows landing area in opposite corner, outside overflow-hidden */}
        {(() => {
          const lastLanded = currentTurnThrows.filter(t => !flyingDarts.some(d => d.id === `${t.playerSocketId}-${t.timestamp}`));
          if (lastLanded.length === 0) return null;
          const last = lastLanded[lastLanded.length - 1];
          const pos = last.position || { x: 50, y: 50 };

          const zoom = 3.5;
          const size = 90;
          // Offset so the dart position is centered in the viewport
          const offsetX = -(pos.x * zoom * (BOARD_SIZE / 100) - size / 2);
          const offsetY = -(pos.y * zoom * (BOARD_SIZE / 100) - size / 2);

          // Place in the corner opposite to where the dart landed
          const isLeft = pos.x < 50;
          const isTop = pos.y < 50;
          const cornerStyle = {
            position: 'absolute',
            width: size,
            height: size,
            ...(isTop ? { bottom: 0 } : { top: 0 }),
            ...(isLeft ? { right: 0 } : { left: 0 }),
            zIndex: 40,
          };

          return (
            <div style={cornerStyle} className="overflow-hidden rounded border-2 border-blue-400 shadow-lg">
              {/* Zoomed board */}
              <div style={{
                width: BOARD_SIZE * zoom,
                height: BOARD_SIZE * zoom,
                backgroundImage: `url(${boardUrl})`,
                backgroundSize: '100% 100%',
                transform: `translate(${offsetX}px, ${offsetY}px)`,
                position: 'absolute',
                top: 0,
                left: 0,
              }} />
              {/* Crosshair at center of zoom */}
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
                <div style={{ position: 'relative', width: 0, height: 0 }}>
                  <div style={{ position: 'absolute', width: 10, height: 2, backgroundColor: '#FDE047', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', boxShadow: '0 0 0 1px black, 0 0 4px rgba(255,255,0,0.8)' }} />
                  <div style={{ position: 'absolute', width: 2, height: 10, backgroundColor: '#FDE047', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', boxShadow: '0 0 0 1px black, 0 0 4px rgba(255,255,0,0.8)' }} />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Instruction */}
      {isMyTurn && phase === 'idle' && throwsDoneThisTurn < 3 && (
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          {isAroundTheWorld
            ? `Aim for ${atwTargetLabel(currentPlayerObj?.target ?? 1)} — dart ${throwsDoneThisTurn + 1} of 3`
            : `Click the board to aim dart ${throwsDoneThisTurn + 1} of 3`}
        </p>
      )}

      {/* Dart scores this turn - visible to all players */}
      {currentTurnThrows.length > 0 && (() => {
        const isBustTurn = !isAroundTheWorld && currentTurnThrows.some(t => t.isBust);
        return (
          <div className={`w-full max-w-sm rounded-lg shadow border p-3 ${
            isBustTurn
              ? 'bg-red-50 dark:bg-red-950 border-red-400 dark:border-red-600'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            {isBustTurn && (
              <div className="text-center mb-2">
                <span className="inline-block px-4 py-1 bg-red-600 text-white text-lg font-black rounded-full tracking-widest uppercase shadow">BUST!</span>
              </div>
            )}
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 text-center uppercase tracking-wide">
              {currentPlayerObj?.username || 'Player'}&apos;s Darts This Turn
            </div>
            <div className="flex justify-center gap-3">
              {currentTurnThrows.map((t, i) => {
                const isMiss = t.ring === 'MISS';
                const isThisBust = t.isBust;
                if (isAroundTheWorld) {
                  const hit = t.atwAdvanced;
                  const tgt = t.atwTarget;
                  return (
                    <div key={i} className={`flex flex-col items-center px-4 py-2 rounded border ${
                      hit
                        ? 'bg-green-50 dark:bg-green-900 border-green-300 dark:border-green-600'
                        : 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700'
                    }`}>
                      <span className={`text-2xl font-black ${hit ? 'text-green-600 dark:text-green-300' : 'text-red-500 dark:text-red-400'}`}>
                        {hit ? '✓' : '✗'}
                      </span>
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{atwTargetLabel(tgt)}</span>
                    </div>
                  );
                }
                const label = ringLabel(t.ring, t.sector);
                return (
                  <div key={i} className={`flex flex-col items-center px-4 py-2 rounded border ${
                    isThisBust
                      ? 'bg-red-100 dark:bg-red-900 border-red-400 dark:border-red-600'
                      : isMiss
                        ? 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700'
                        : 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700'
                  }`}>
                    <span className={`text-lg font-bold ${
                      isThisBust ? 'text-red-600 dark:text-red-300 line-through' : 'text-gray-900 dark:text-white'
                    }`}>{label}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">+{t.score}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
              {isBustTurn
                ? <span className="text-red-600 dark:text-red-400">Score reverted!</span>
                : isAroundTheWorld
                  ? `Targets hit: ${currentTurnThrows.filter(t => t.atwAdvanced).length}`
                  : `Turn total: ${currentTurnThrows.reduce((s, t) => s + (t.score || 0), 0)}`
              }
            </div>
          </div>
        );
      })()}

      {/* Current player + scoreboard */}
      <div className="w-full max-w-sm space-y-3">
        {currentPlayerObj && (
          <div className="bg-yellow-50 dark:bg-yellow-900 border-2 border-yellow-300 dark:border-yellow-600 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1">Now throwing</div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{currentPlayerObj.username}</div>
            <div className="flex justify-center gap-6 mt-2 text-sm">
              {isAroundTheWorld ? (
                <>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">Target</div>
                    <div className="font-black text-yellow-600 dark:text-yellow-300 text-xl">
                      {atwTargetLabel(currentPlayerObj.target ?? 1)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">Progress</div>
                    <div className="font-bold text-green-600 dark:text-green-300">
                      {currentPlayerObj.progress ?? 0} / 21
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">Score left</div>
                    <div className="font-bold text-green-600 dark:text-green-300">{currentPlayerObj.score}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">Darts thrown</div>
                    <div className="font-bold text-indigo-600 dark:text-indigo-300">{throwsDoneThisTurn} / 3</div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-3">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">All Players</div>
          <div className="space-y-1">
            {(gameState?.players || []).map(p => {
              const isCurrent = p.socketId === currentPlayer;
              const isMe = p.socketId === myId;
              return (
                <div key={p.socketId} className={`flex justify-between items-center px-3 py-1.5 rounded text-sm ${isCurrent ? 'bg-yellow-100 dark:bg-yellow-800 font-semibold' : 'bg-gray-50 dark:bg-gray-700'}`}>
                  <span className="text-gray-800 dark:text-gray-200">
                    {p.username}{isMe ? ' (you)' : ''}{isCurrent ? ' ▶' : ''}
                  </span>
                  {isAroundTheWorld ? (
                    <span className="font-bold text-gray-900 dark:text-white text-right">
                      <span className="text-yellow-600 dark:text-yellow-300">{atwTargetLabel(p.target ?? 1)}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">({p.progress ?? 0}/21)</span>
                    </span>
                  ) : (
                    <span className="font-bold text-gray-900 dark:text-white">{p.score}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
DartsGame.Guide = function DartsGameGuide() {
  return (
    <div className="space-y-4 text-sm">
      <h3 className="font-bold text-base">How to Play Darts</h3>

      <div>
        <h4 className="font-semibold mb-1">Aiming</h4>
        <ul className="list-disc ml-5 space-y-1">
          <li>Click anywhere on the dartboard to set your aim.</li>
          <li>A timing bar will appear — click again when the moving bar is near the centre green zone.</li>
          <li>The closer to the centre you click, the more accurate your throw.</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold mb-1">Game Modes</h4>
        <ul className="list-disc ml-5 space-y-1">
          <li><span className="font-medium">101 / 301 / 501 / 701</span> — Start at that score and count down to exactly zero. You must finish on a double or the bull's-eye.</li>
          <li><span className="font-medium">Around the World</span> — Hit targets 1 through 20 in order, then finish on the bull. Doubles and triples count as a single hit. First to complete all 21 targets wins.</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold mb-1">Scoring (Countdown)</h4>
        <ul className="list-disc ml-5 space-y-1">
          <li>Single ring scores the sector number.</li>
          <li>Double ring (outer thin ring) scores 2× the sector.</li>
          <li>Triple ring (inner thin ring) scores 3× the sector.</li>
          <li>Outer bull scores 25, inner bull scores 50.</li>
          <li><span className="font-medium">Bust</span> — if a throw takes your score below zero, or to zero without a valid double-finish, your score resets to what it was at the start of your turn.</li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold mb-1">Turns</h4>
        <ul className="list-disc ml-5 space-y-1">
          <li>Each player throws 3 darts per turn.</li>
          <li>All players can see every dart land in real time.</li>
          <li>A zoomed preview shows exactly where each dart landed.</li>
        </ul>
      </div>
    </div>
  );
};